import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;

  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string;

  handleSearchLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleUpdateSearchLinkedinAccount(): void;
}

const EditSearchLinkedinAccountDialog: React.FC<Props> = ({
  open,
  loading,
  linkedinAccounts,
  selectedLinkedinAccount,
  handleSearchLinkedinAccountChange,
  handleClose,
  handleUpdateSearchLinkedinAccount,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const options = useMemo(() => linkedinAccounts.map((account) => ({ label: account.userEmail, value: account._id })), [linkedinAccounts]);

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('changeLinkedinAccount')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {!_.isEmpty(options) ? (
            <PaiSelect value={selectedLinkedinAccount} label={t('selectLinkedinAccountForSearch')} onChange={handleSearchLinkedinAccountChange}>
              {options.map((option) => (
                <PaiMenuItem key={option.value} value={option.value}>
                  {option.label}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('ok')}
          action={handleUpdateSearchLinkedinAccount}
          size={'small'}
          border={'rounded'}
          disabled={selectedLinkedinAccount.trim() === ''}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    fontSize: theme.typography.pxToRem(11),
    color: 'red',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditSearchLinkedinAccountDialog;
